import { Dialog, DialogActions, DialogContent, DialogTitle } from 'components/Dialog';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '@mui/material';
import { closeModal } from 'features/modal/modalSlice';
import { closeUnlessEscape } from 'utils/closeUnlessEscape';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/hooks';
import { useEffect, useId } from 'react';
import { usePolls } from 'features/polls/usePolls';
import PollForm, { PollFormValues } from 'features/polls/components/PollForm';
import { createPollRequested } from 'features/polls/actions';

const CreatePollModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const { isFeatureAvailable, canManagePolls } = usePolls();

  const methods = useForm<PollFormValues>({
    mode: 'onChange',
    defaultValues: {
      question: '',
      type: 'single',
      choices: [],
      anonymous: false,
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const modalShouldBeClosed = !isFeatureAvailable || !canManagePolls;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('createPoll'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const createPoll: SubmitHandler<PollFormValues> = (data) => {
    dispatch(
      createPollRequested({
        question: data.question,
        type: data.type,
        choices: data.type === 'free' ? undefined : data.choices.map((choice) => choice.text),
        anonymous: data.anonymous,
      })
    );
    dispatch(closeModal('createPoll'));
  };

  const handleCancel = () => {
    dispatch(closeModal('createPoll'));
  };

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      onClose={closeUnlessEscape(handleCancel)}
      disableRestoreFocus
    >
      <DialogTitle id={`${modalId}-title`}>{t('polls.create_poll.title')}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <PollForm />
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="grayscale" onClick={handleCancel}>
          {t('polls.create_poll.btn_cancel_label')}
        </Button>
        <Button type="button" onClick={handleSubmit(createPoll)} disabled={!isValid}>
          {t('polls.create_poll.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePollModal;
